import React, { useEffect, useState } from 'react';
import { getProjects, deleteProject, updateProject, getProject } from '../../utils/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import ProjectNameInput from './ProjectNameInput';
import ProjectDetails from './ProjectDetails';
import { FiGrid, FiList, FiPlus, FiArchive, FiUpload, FiTrash2, FiImage, FiArrowUp, FiArrowDown, FiInfo } from 'react-icons/fi';
import '../Projects/Projects.css';

/**
 * ProjectList component to display and manage saved projects
 * @param {Object} props - Component props
 * @param {Function} props.onSelectProject - Function to call when a project is selected
 * @returns {JSX.Element} ProjectList component
 */
const ProjectList = ({ onSelectProject }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(true); // Always show archived projects since toggle is removed
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortField, setSortField] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [editingProject, setEditingProject] = useState(null);
  const [activeSearch, setActiveSearch] = useState('');
  const [archivedSearch, setArchivedSearch] = useState('');
  const [filterMode, setFilterMode] = useState('my'); // 'all' or 'my'
  const [loadingProject, setLoadingProject] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const { user } = useAuth();

  // Fun loading messages
  const loadingMessages = [
    "Reticulating splines...",
    "Moving mountains...",
    "Calculating sensor positions...",
    "Drawing floorplans...",
    "Measuring distances...",
    "Configuring sensors...",
    "Mapping entry points...",
    "Analyzing spatial data...",
    "Computing optimal layouts...",
    "Rendering blueprint details..."
  ];

  // Load projects when component mounts or user changes
  useEffect(() => {
    if (user) {
      loadProjects();
    } else {
      setProjects([]);
      setLoading(false);
    }
  }, [user]);

  // Change loading message every 1.5 seconds
  useEffect(() => {
    let interval;
    if (loadingProject) {
      // Set initial message
      setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)]);
      
      // Change message every 1.5 seconds
      interval = setInterval(() => {
        setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)]);
      }, 1500);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loadingProject]);

  /**
   * Load projects from Supabase
   */
  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProjects();
      
      // Set default archived status if not present
      const updatedData = data.map(project => ({
        ...project,
        archived: project.archived || false
      }));
      
      setProjects(updatedData);
    } catch (error) {
      setError('Failed to load projects');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle project selection with loading full data
   * @param {Object} project - Basic project data
   */
  const handleSelectProject = async (project) => {
    try {
      setLoadingProject(true);
      
      // Fetch complete project data including JSON fields
      const fullProjectData = await getProject(project.id);
      
      if (fullProjectData) {
        onSelectProject(fullProjectData);
      } else {
        throw new Error('Failed to load complete project data');
      }
    } catch (error) {
      setError('Failed to load complete project data');
      console.error(error);
    } finally {
      setLoadingProject(false);
    }
  };

  /**
   * Delete a project
   * @param {string} id - Project ID
   * @param {Event} e - Click event
   */
  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        setError(null);
        await deleteProject(id);
        setProjects(projects.filter(project => project.id !== id));
      } catch (error) {
        setError('Failed to delete project');
        console.error(error);
      }
    }
  };

  /**
   * Update project name
   * @param {string} id - Project ID
   * @param {string} newName - New project name
   */
  const handleNameChange = async (id, newName) => {
    try {
      setError(null);
      const project = projects.find(p => p.id === id);
      if (!project) return;

      // Update project in database
      await updateProject(id, { name: newName });
      
      // Update local state
      setProjects(projects.map(p => 
        p.id === id ? { ...p, name: newName } : p
      ));
    } catch (error) {
      setError('Failed to update project name');
      console.error(error);
    }
  };

  /**
   * Toggle project archived status
   * @param {string} id - Project ID
   * @param {boolean} currentStatus - Current archived status
   * @param {Event} e - Click event (optional)
   */
  const toggleArchived = async (id, currentStatus, e) => {
    if (e) e.stopPropagation();
    try {
      setError(null);
      const newArchivedStatus = !currentStatus;
      
      // Update project in database
      await updateProject(id, { archived: newArchivedStatus });
      
      // Update local state
      setProjects(projects.map(p => 
        p.id === id ? { ...p, archived: newArchivedStatus } : p
      ));
    } catch (error) {
      setError('Failed to update project status');
      console.error(error);
    }
  };

  /**
   * Open project details for editing
   * @param {Object} project - Project to edit
   * @param {Event} e - Click event
   */
  const handleEditDetails = (project, e) => {
    if (e) e.stopPropagation();
    setEditingProject(project);
  };

  /**
   * Update project details
   * @param {Object} updatedProject - Updated project data
   */
  const handleUpdateProject = async (updatedProject) => {
    try {
      setError(null);
      
      // Update project in database
      await updateProject(updatedProject.id, {
        customer: updatedProject.customer,
        location: updatedProject.location,
        installation_requirements: updatedProject.installation_requirements
      });
      
      // Update local state
      setProjects(projects.map(p => 
        p.id === updatedProject.id ? {...p, ...updatedProject} : p
      ));
      
      setEditingProject(null);
    } catch (error) {
      setError('Failed to update project details');
      console.error(error);
    }
  };

  // Function to handle sorting
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle sort direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter and sort active projects with search
  const activeProjects = projects
    .filter(project => !project.archived)
    .filter(project => {
      // Apply filter mode (all projects or my projects)
      if (filterMode === 'my' && user) {
        return project.user_id === user.id;
      }
      return true;
    })
    .filter(project => {
      if (!activeSearch) return true;
      const searchTerm = activeSearch.toLowerCase();
      const authorDisplay = project.author || (user && project.user_id === user.id ? 'me' : '');
      return (
        (project.name || '').toLowerCase().includes(searchTerm) ||
        (project.customer || '').toLowerCase().includes(searchTerm) ||
        (project.location || '').toLowerCase().includes(searchTerm) ||
        authorDisplay.toLowerCase().includes(searchTerm)
      );
    })
    .sort((a, b) => {
      if (sortField === 'name') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortDirection === 'asc' 
          ? nameA.localeCompare(nameB) 
          : nameB.localeCompare(nameA);
      }
      if (sortField === 'updated_at') {
        const dateA = a.updated_at ? new Date(a.updated_at) : new Date(0);
        const dateB = b.updated_at ? new Date(b.updated_at) : new Date(0);
        return sortDirection === 'asc' 
          ? dateA - dateB 
          : dateB - dateA;
      }
      return 0;
    });
    
  // Filter and sort archived projects with search
  const archivedProjects = projects
    .filter(project => project.archived)
    .filter(project => {
      if (!archivedSearch) return true;
      const searchTerm = archivedSearch.toLowerCase();
      const authorDisplay = project.author || (user && project.user_id === user.id ? 'me' : '');
      return (
        (project.name || '').toLowerCase().includes(searchTerm) ||
        (project.customer || '').toLowerCase().includes(searchTerm) ||
        (project.location || '').toLowerCase().includes(searchTerm) ||
        authorDisplay.toLowerCase().includes(searchTerm)
      );
    })
    .sort((a, b) => {
      if (sortField === 'name') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortDirection === 'asc' 
          ? nameA.localeCompare(nameB) 
          : nameB.localeCompare(nameA);
      }
      if (sortField === 'updated_at') {
        const dateA = a.updated_at ? new Date(a.updated_at) : new Date(0);
        const dateB = b.updated_at ? new Date(b.updated_at) : new Date(0);
        return sortDirection === 'asc' 
          ? dateA - dateB 
          : dateB - dateA;
      }
      return 0;
    });

  // Get counts
  const activeProjectsCount = projects.filter(p => !p.archived).length;
  const archivedProjectsCount = projects.filter(p => p.archived).length;

  // Get counts for filter buttons
  const allActiveProjectsCount = projects.filter(p => !p.archived).length;
  const myProjectsCount = projects.filter(p => !p.archived && p.user_id === user?.id).length;

  // Handle view toggle
  const toggleViewMode = (mode) => {
    setViewMode(mode);
  };

  // Format date to display in a friendly way
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Format for today
    if (date.toDateString() === now.toDateString()) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Format for yesterday
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Format for older dates
    return date.toLocaleDateString([], { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Render sort arrow based on current sort field and direction
  const renderSortArrow = (field) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <FiArrowUp className="sort-icon" /> 
      : <FiArrowDown className="sort-icon" />;
  };

  // Add a new function to render installation requirements summary
  const renderInstallationSummary = (project, inTable = false) => {
    if (!project.installation_requirements) return inTable ? '-' : null;
    
    const req = project.installation_requirements;
    const details = [];
    
    if (req.unionLabor) details.push('Union Labor');
    details.push(req.fullInstall ? 'Full Install' : 'Last Mile Only');
    if (req.conduitRequired) details.push('Conduit');
    if (req.jboxRequired) details.push('J-Box');
    if (req.quoteQAd) details.push('QA Completed');
    
    if (details.length === 0) return inTable ? '-' : null;
    
    if (inTable) {
      return details.join(', ');
    }
    
    return (
      <div className="metadata-item">
        <span className="label">Installation:</span>
        <span>{details.join(', ')}</span>
      </div>
    );
  };

  // Render Grid View
  const renderGridView = (projectsList, isArchived) => {
    return (
      <div className={`project-grid ${isArchived ? 'archived' : ''}`}>
        {projectsList.map(project => (
          <div 
            key={project.id} 
            className={`project-card ${project.archived ? 'archived' : ''}`}
            onClick={() => handleSelectProject(project)}
          >
            <div className="project-image">
              {project.image_url ? (
                <img src={project.image_url} alt={project.name} />
              ) : (
                <div className="placeholder-image">
                  <FiImage size={36} />
                  <span>No preview available</span>
                </div>
              )}
            </div>
            
            <div className="project-details">
              <ProjectNameInput 
                initialName={project.name} 
                onNameChange={(newName) => handleNameChange(project.id, newName)}
              />
              
              {(project.customer || project.location || project.author) && (
                <div className="project-metadata">
                  {project.customer && (
                    <div className="metadata-item">
                      <span className="label">Customer:</span>
                      <span>{project.customer}</span>
                    </div>
                  )}
                  {project.location && (
                    <div className="metadata-item">
                      <span className="label">Location:</span>
                      <span>{project.location}</span>
                    </div>
                  )}
                  {project.author || user && project.user_id === user.id && (
                    <div className="metadata-item">
                      <span className="label">Author:</span>
                      <span>{project.author || (user && project.user_id === user.id ? 'me' : '-')}</span>
                    </div>
                  )}
                </div>
              )}
              
              {renderInstallationSummary(project)}
              
              <div className="project-date">
                Last updated {formatDate(project.updated_at)}
              </div>
              
              <div className="project-actions">
                <button
                  className="action-button details"
                  onClick={(e) => handleEditDetails(project, e)}
                  title="Install q's"
                >
                  <FiInfo /> Install q's
                </button>
                
                {isArchived ? (
                  <button 
                    className="unarchive-button" 
                    onClick={(e) => toggleArchived(project.id, project.archived, e)}
                  >
                    <FiUpload style={{ marginRight: '4px' }} /> Unarchive
                  </button>
                ) : (
                  <button 
                    className="archive-button" 
                    onClick={(e) => toggleArchived(project.id, project.archived, e)}
                  >
                    <FiArchive style={{ marginRight: '4px' }} /> Archive
                  </button>
                )}
                
                <button 
                  className="delete-project-button" 
                  onClick={(e) => handleDelete(project.id, e)}
                >
                  <FiTrash2 style={{ marginRight: '4px' }} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render List View
  const renderListView = (projectsList, isArchived) => {
    return (
      <div className={`project-list-view ${isArchived ? 'archived' : ''}`}>
        <table className="project-table">
          <thead>
            <tr>
              <th className="sortable-header" onClick={() => handleSort('name')}>
                Project Name
                {sortField === 'name' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? <FiArrowUp /> : <FiArrowDown />}
                  </span>
                )}
              </th>
              <th>Preview</th>
              <th className="sortable-header" onClick={() => handleSort('updated_at')}>
                Last Updated
                {sortField === 'updated_at' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? <FiArrowUp /> : <FiArrowDown />}
                  </span>
                )}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projectsList.map(project => (
              <tr key={project.id} onClick={() => handleSelectProject(project)}>
                <td className="project-name-cell">
                  <ProjectNameInput 
                    initialName={project.name || ''} 
                    onNameChange={(newName) => handleNameChange(project.id, newName)}
                  />
                </td>
                <td className="project-thumb-cell">
                  {project.image_url ? (
                    <img 
                      src={project.image_url} 
                      alt={project.name} 
                      className="project-thumbnail"
                    />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <FiImage size={16} />
                    </div>
                  )}
                </td>
                <td className="project-date-cell">
                  {formatDate(project.updated_at)}
                </td>
                <td className="project-actions-cell">
                  <div className="list-actions">
                    <button
                      className="action-button details"
                      onClick={(e) => handleEditDetails(project, e)}
                      title="Install q's"
                    >
                      <FiInfo /> Install q's
                    </button>
                    
                    {isArchived ? (
                      <button 
                        className="unarchive-button" 
                        onClick={(e) => toggleArchived(project.id, project.archived, e)}
                      >
                        <FiUpload style={{ marginRight: '4px' }} /> Unarchive
                      </button>
                    ) : (
                      <button 
                        className="archive-button" 
                        onClick={(e) => toggleArchived(project.id, project.archived, e)}
                      >
                        <FiArchive style={{ marginRight: '4px' }} /> Archive
                      </button>
                    )}
                    <button 
                      className="delete-project-button" 
                      onClick={(e) => handleDelete(project.id, e)}
                    >
                      <FiTrash2 style={{ marginRight: '4px' }} /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render projects section
  const renderProjectsSection = (projectsList, isArchived) => {
    return (
      <div className={`projects-section ${isArchived ? 'archived-section' : ''}`}>
        <div className="projects-section-header">
          <div className="section-title-container">
            <h3 className="projects-section-title">
              {isArchived ? 'Archived Projects' : 'Active Projects'}
              <span className="projects-count">
                {isArchived ? archivedProjectsCount : activeProjectsCount}
              </span>
            </h3>
            
            {!isArchived && (
              <div className="filter-and-view-container">
                <div className="filter-toggle">
                  <button 
                    className={`filter-toggle-button ${filterMode === 'all' ? 'active' : ''}`}
                    onClick={() => setFilterMode('all')}
                  >
                    All Projects
                    <span className="filter-count">{allActiveProjectsCount}</span>
                  </button>
                  <button 
                    className={`filter-toggle-button ${filterMode === 'my' ? 'active' : ''}`}
                    onClick={() => setFilterMode('my')}
                  >
                    My Projects
                    <span className="filter-count">{myProjectsCount}</span>
                  </button>
                </div>
                <div className="view-toggle-text">
                  <span 
                    className={`view-option ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('grid')}
                  >
                    Grid
                  </span>
                  <span 
                    className={`view-option ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('list')}
                  >
                    List
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="search-container">
          <input
            type="text"
            placeholder="Search projects..."
            value={isArchived ? archivedSearch : activeSearch}
            onChange={(e) => isArchived ? setArchivedSearch(e.target.value) : setActiveSearch(e.target.value)}
            className="search-input"
          />
        </div>

        {projectsList.length === 0 ? (
          <div className="section-empty">
            {isArchived ? (
              <p>No archived projects found.</p>
            ) : (
              <p>No active projects found. Upload a file to create your first project.</p>
            )}
          </div>
        ) : (
          viewMode === 'grid' 
            ? renderGridView(projectsList, isArchived)
            : renderListView(projectsList, isArchived)
        )}
      </div>
    );
  };

  if (!user) {
    return (
      <div className="projects-auth-message">
        <p>Please log in to view your projects.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="projects-loading">
        <div className="spinner"></div>
        <p>Loading projects...</p>
      </div>
    );
  }

  return (
    <div className="project-list">
      {error && <div className="project-error">{error}</div>}
      
      <header className="projects-header">
        <div className="header-content">
          <h2>Projects</h2>
          <p className="drag-drop-helper">Drag and drop an image or PDF in to create a new project.</p>
        </div>
      </header>
      
      {loadingProject && (
        <div className="project-loading-overlay">
          <div className="project-loading-content">
            <div className="spinner"></div>
            <p>{loadingMessage}</p>
          </div>
        </div>
      )}
      
      {renderProjectsSection(activeProjects, false)}
      
      {showArchived && archivedProjectsCount > 0 && renderProjectsSection(archivedProjects, true)}

      {editingProject && (
        <div className="modal-overlay" onClick={() => setEditingProject(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <ProjectDetails
              project={editingProject}
              onUpdate={handleUpdateProject}
              onClose={() => setEditingProject(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectList; 
